import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AppBar,
  Box,
  Button,
  Dialog,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { lateralSlack, margins, occupiedWidth, round, verticalPitch } from '../../../utils/etiqueta/geometry';
import { MACROS, defaultSampleData } from '../../../utils/etiqueta/macros';
import {
  montarArquivoZetiq,
  nomeArquivoZetiq,
  payloadDoLayout,
  uid,
} from '../../../utils/etiqueta/modelo';
import { PRINTERS, getPrinter } from '../../../utils/etiqueta/printers';
import { validateModel } from '../../../utils/etiqueta/validar';
import EtiquetaCanvas from './EtiquetaCanvas';
import { useEditorEtiqueta } from './useEditorEtiqueta';

const PASSOS = ['Bobina', 'Desenho', 'Salvar'];

function CampoNumero({ label, value, onChange, min, step = 0.1, suffix = 'mm', disabled }) {
  const [txt, setTxt] = useState(String(value));
  useEffect(() => setTxt(String(value)), [value]);
  const commit = () => {
    const n = Number(String(txt).replace(',', '.'));
    if (Number.isFinite(n) && (min === undefined || n >= min)) onChange(n);
    else setTxt(String(value));
  };
  return (
    <TextField
      label={label}
      size="small"
      value={txt}
      disabled={disabled}
      onChange={(e) => setTxt(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') commit(); }}
      inputProps={{ step }}
      InputProps={{ endAdornment: suffix ? <InputAdornment position="end">{suffix}</InputAdornment> : undefined }}
      fullWidth
    />
  );
}

function novoTexto(name, content, fontSize = 3) {
  return {
    id: uid(),
    type: 'text',
    name,
    content,
    x: 2,
    y: 2,
    w: 30,
    h: +(fontSize * 1.3).toFixed(2),
    fontSize,
    align: 'left',
    bold: false,
    maxLines: 1,
    font: '0',
    rotation: 0,
  };
}

const FERRAMENTAS = [
  { label: 'Texto', make: () => novoTexto('Texto', 'Texto') },
  { label: 'Descrição', make: () => novoTexto('Descrição', '{produto1.descricao-1}') },
  { label: 'Preço', make: () => novoTexto('Preço', '{produto1.precovarejo}', 6) },
  { label: 'Data e hora', make: () => novoTexto('Data/hora', '{produto1.dataimpressao} {produto1.horaimpressao}', 2.2) },
  {
    label: 'Código de barras',
    make: () => ({
      id: uid(), type: 'barcode', name: 'Código de barras', symbology: 'EAN13', content: '{produto1.ean}',
      x: 2, y: 2, w: 38, h: 12, showText: true, moduleWidth: 2, rotation: 0,
    }),
  },
  {
    label: 'Linha',
    make: () => ({ id: uid(), type: 'line', name: 'Linha', orientation: 'h', x: 2, y: 5, w: 30, h: 0.4, thickness: 0.4, rotation: 0 }),
  },
  {
    label: 'Retângulo',
    make: () => ({ id: uid(), type: 'rect', name: 'Retângulo', x: 1, y: 1, w: 20, h: 10, thickness: 0.3, rotation: 0 }),
  },
];

function Propriedades({ ed, issues }) {
  const el = ed.selected;
  if (!el) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" gutterBottom>Propriedades</Typography>
        <Typography variant="body2" color="text.secondary">
          Clique num elemento para mover. Setas deslocam 0,5 mm, Shift 0,1 mm. Delete exclui. Ctrl+D duplica.
        </Typography>
      </Box>
    );
  }
  const up = (patch) => ed.updateElement(el.id, patch);
  const { labelWidth: lw, labelHeight: lh } = ed.model.roll;
  const meus = issues.filter((i) => i.elementId === el.id);
  return (
    <Stack spacing={1.5} sx={{ p: 2 }}>
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          onClick={() => {
            const copia = { ...el, id: uid(), name: `${el.name} (cópia)`, x: round(el.x + 1), y: round(el.y + 1) };
            ed.setModel({ ...ed.model, elements: [...ed.model.elements, copia] });
            ed.select(copia.id);
          }}
        >
          Duplicar
        </Button>
        <Button
          size="small"
          color="error"
          onClick={() => {
            ed.setModel({ ...ed.model, elements: ed.model.elements.filter((item) => item.id !== el.id) });
            ed.select(null);
          }}
        >
          Excluir
        </Button>
      </Stack>
      <TextField label="Nome" size="small" value={el.name} onChange={(e) => up({ name: e.target.value })} />
      {(el.type === 'text' || el.type === 'barcode') && (
        <>
          <TextField
            label={el.type === 'text' ? 'Texto e macros' : 'Conteúdo'}
            size="small"
            value={el.content}
            onChange={(e) => up({ content: e.target.value })}
            multiline
            minRows={2}
          />
          <TextField
            select
            label="Inserir macro"
            size="small"
            value=""
            onChange={(e) => up({ content: el.type === 'barcode' ? e.target.value : `${el.content}${e.target.value}` })}
          >
            <MenuItem value="">Escolha</MenuItem>
            {MACROS.map((m) => (
              <MenuItem key={m.key} value={`{${m.key}}`}>{m.label}</MenuItem>
            ))}
          </TextField>
        </>
      )}
      {el.type === 'text' && (
        <>
          <CampoNumero
            label="Fonte"
            value={el.fontSize}
            min={0.5}
            onChange={(fontSize) => up({ fontSize, h: Math.max(el.h, round(fontSize * 1.3 * el.maxLines)) })}
          />
          <CampoNumero
            label="Linhas"
            value={el.maxLines}
            min={1}
            step={1}
            suffix=""
            onChange={(v) => up({ maxLines: Math.max(1, Math.min(20, Math.round(v))) })}
          />
          <ToggleButtonGroup
            exclusive
            size="small"
            value={el.align}
            onChange={(_, align) => { if (align) up({ align }); }}
          >
            <ToggleButton value="left">Esq.</ToggleButton>
            <ToggleButton value="center">Centro</ToggleButton>
            <ToggleButton value="right">Dir.</ToggleButton>
          </ToggleButtonGroup>
        </>
      )}
      {el.type === 'barcode' && (
        <>
          <TextField select label="Simbologia" size="small" value={el.symbology} onChange={(e) => up({ symbology: e.target.value })}>
            <MenuItem value="EAN13">EAN-13</MenuItem>
            <MenuItem value="CODE128">Code 128</MenuItem>
          </TextField>
          <CampoNumero
            label="Módulo"
            value={el.moduleWidth}
            min={1}
            step={1}
            suffix="dots"
            onChange={(v) => up({ moduleWidth: Math.max(1, Math.min(10, Math.round(v))) })}
          />
          <Button size="small" variant={el.showText ? 'contained' : 'outlined'} onClick={() => up({ showText: !el.showText })}>
            {el.showText ? 'Número visível' : 'Sem número'}
          </Button>
        </>
      )}
      {(el.type === 'line' || el.type === 'rect') && (
        <CampoNumero
          label="Espessura"
          value={el.thickness}
          min={0.1}
          onChange={(thickness) => up(el.type === 'line' && el.orientation === 'h' ? { thickness, h: thickness } : el.type === 'line' ? { thickness, w: thickness } : { thickness })}
        />
      )}
      <Stack direction="row" spacing={1}>
        <CampoNumero label="X" value={el.x} onChange={(x) => up({ x })} />
        <CampoNumero label="Y" value={el.y} onChange={(y) => up({ y })} />
      </Stack>
      <Stack direction="row" spacing={1}>
        <CampoNumero label="Largura" value={el.w} min={0.1} onChange={(w) => up({ w })} />
        <CampoNumero label="Altura" value={el.h} min={0.1} onChange={(h) => up({ h })} />
      </Stack>
      <Stack direction="row" flexWrap="wrap" gap={0.5}>
        <Button size="small" onClick={() => up({ x: 0 })}>Esquerda</Button>
        <Button size="small" onClick={() => up({ x: round((lw - el.w) / 2) })}>Centro</Button>
        <Button size="small" onClick={() => up({ x: round(lw - el.w) })}>Direita</Button>
        <Button size="small" onClick={() => up({ y: 0 })}>Topo</Button>
        <Button size="small" onClick={() => up({ y: round((lh - el.h) / 2) })}>Meio</Button>
        <Button size="small" onClick={() => up({ y: round(lh - el.h) })}>Base</Button>
      </Stack>
      {el.type !== 'line' && el.type !== 'rect' && (
        <TextField
          select
          label="Rotação"
          size="small"
          value={String(el.rotation)}
          onChange={(e) => {
            const rotation = Number(e.target.value);
            const swap = (rotation % 180 !== 0) !== (el.rotation % 180 !== 0);
            up(swap ? { rotation, w: el.h, h: el.w } : { rotation });
          }}
        >
          {[0, 90, 180, 270].map((r) => <MenuItem key={r} value={String(r)}>{r}°</MenuItem>)}
        </TextField>
      )}
      {meus.length > 0 && (
        <Alert severity={meus.some((i) => i.level === 'erro') ? 'error' : 'warning'}>
          {meus.map((i) => i.msg).join(' ')}
        </Alert>
      )}
    </Stack>
  );
}

export default function EditorModeloEtiqueta({
  modeloId,
  descricaoInicial,
  layoutInicial,
  aviso,
  erro,
  onClose,
  onSave,
  saving,
}) {
  const ed = useEditorEtiqueta(layoutInicial);
  const [passo, setPasso] = useState(0);
  const [descricao, setDescricao] = useState(descricaoInicial || '');
  const [nomeAlterado, setNomeAlterado] = useState(false);
  const [zoom, setZoom] = useState(2);
  const [grid, setGrid] = useState(true);
  const [snap, setSnap] = useState(true);
  const [mostrarMacros, setMostrarMacros] = useState(false);
  const sample = useMemo(() => defaultSampleData(), []);
  const printer = getPrinter(ed.model.printerId);
  const issues = useMemo(() => validateModel(ed.model, sample), [ed.model, sample]);
  const issueIds = useMemo(() => new Set(issues.filter((i) => i.elementId).map((i) => i.elementId)), [issues]);
  const erros = issues.filter((i) => i.level === 'erro');
  const roll = ed.model.roll;
  const margem = margins(roll);
  const template = useMemo(() => (erros.length ? '' : payloadDoLayout(descricao || 'etiqueta', ed.model).template), [erros.length, descricao, ed.model]);

  useEffect(() => {
    if (passo !== 1) return undefined;
    const onKey = (e) => {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || e.target.isContentEditable) return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) ed.redo();
        else ed.undo();
        return;
      }
      if (mod && e.key.toLowerCase() === 'y') { e.preventDefault(); ed.redo(); return; }
      const el = ed.selected;
      if (!el) return;
      if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        ed.setModel({ ...ed.model, elements: ed.model.elements.filter((item) => item.id !== el.id) });
        ed.select(null);
        return;
      }
      if (mod && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        const copia = { ...el, id: uid(), x: round(el.x + 1), y: round(el.y + 1) };
        ed.setModel({ ...ed.model, elements: [...ed.model.elements, copia] });
        ed.select(copia.id);
        return;
      }
      const delta = e.shiftKey ? 0.1 : 0.5;
      const move = { ArrowLeft: [-delta, 0], ArrowRight: [delta, 0], ArrowUp: [0, -delta], ArrowDown: [0, delta] };
      if (move[e.key]) {
        e.preventDefault();
        const [dx, dy] = move[e.key];
        ed.updateElement(el.id, { x: round(el.x + dx, 2), y: round(el.y + dy, 2) });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const setRoll = (patch) => ed.setModel({ ...ed.model, roll: { ...roll, ...patch } });
  const fechar = () => {
    if ((ed.dirty || nomeAlterado) && !window.confirm('Há alterações não salvas. Fechar mesmo assim?')) return;
    onClose();
  };
  const exportar = () => {
    const nome = descricao.trim() || 'etiqueta';
    const arquivo = montarArquivoZetiq({ descricao: nome, layout: ed.model });
    const blob = new Blob([JSON.stringify(arquivo, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nomeArquivoZetiq(nome);
    link.click();
    URL.revokeObjectURL(url);
  };
  const salvar = () => {
    if (!descricao.trim() || erros.length || saving) return;
    onSave({ id: modeloId, ...payloadDoLayout(descricao, ed.model) });
  };

  return (
    <Dialog fullScreen open onClose={fechar} PaperProps={{ sx: { display: 'flex', flexDirection: 'column' } }}>
      <AppBar position="relative" color="default" elevation={1}>
        <Toolbar>
          <IconButton edge="start" onClick={fechar} aria-label="Fechar"><CloseIcon /></IconButton>
          <Box sx={{ flex: 1, minWidth: 0, ml: 1 }}>
            <Typography variant="subtitle1" noWrap>{descricao.trim() || 'Novo modelo gráfico'}</Typography>
            <Typography variant="caption" color="text.secondary">{printer.manufacturer} {printer.model}</Typography>
          </Box>
          <Button startIcon={<FileDownloadIcon />} onClick={exportar}>Exportar .zetiq</Button>
        </Toolbar>
      </AppBar>
      <Box sx={{ p: { xs: 1.5, md: 2 }, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2, overflow: 'auto' }}>
        <Stepper activeStep={passo}>
          {PASSOS.map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
        </Stepper>
        {erro && <Alert severity="error">{erro}</Alert>}
        {aviso && passo === 0 && <Alert severity="info">{aviso}</Alert>}
        {erros.length > 0 && passo === 2 && (
          <Alert severity="error">{erros.map((i) => i.msg).join(' · ')}</Alert>
        )}

        {passo === 0 && (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ overflow: 'auto' }}>
            <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
              <Stack spacing={2}>
                <TextField
                  select
                  label="Impressora"
                  value={ed.model.printerId}
                  onChange={(e) => ed.setModel({ ...ed.model, printerId: e.target.value })}
                >
                  {PRINTERS.map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.manufacturer} {p.model}</MenuItem>
                  ))}
                </TextField>
                <Typography variant="body2" color="text.secondary">{printer.notes}</Typography>
                <TextField label="Nome da bobina" value={roll.name} onChange={(e) => setRoll({ name: e.target.value })} />
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <CampoNumero label="Largura da bobina" value={roll.rollWidth} min={1} onChange={(rollWidth) => setRoll({ rollWidth })} />
                  <CampoNumero label="Largura da etiqueta" value={roll.labelWidth} min={1} onChange={(labelWidth) => setRoll({ labelWidth })} />
                  <CampoNumero label="Altura da etiqueta" value={roll.labelHeight} min={1} onChange={(labelHeight) => setRoll({ labelHeight })} />
                </Stack>
                <ToggleButtonGroup
                  exclusive
                  value={roll.columns}
                  onChange={(_, columns) => { if (columns) setRoll({ columns }); }}
                >
                  {[1, 2, 3].map((c) => <ToggleButton key={c} value={c}>{c} {c === 1 ? 'coluna' : 'colunas'}</ToggleButton>)}
                </ToggleButtonGroup>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <CampoNumero label="Espaço entre colunas" value={roll.hGap} min={0} onChange={(hGap) => setRoll({ hGap })} />
                  <CampoNumero label="Espaço entre fileiras" value={roll.vGap} min={0} onChange={(vGap) => setRoll({ vGap })} />
                </Stack>
                <Button variant={roll.autoCenter ? 'contained' : 'outlined'} onClick={() => setRoll({ autoCenter: !roll.autoCenter })}>
                  {roll.autoCenter ? 'Centralizar na bobina' : 'Margem esquerda manual'}
                </Button>
                {!roll.autoCenter && (
                  <CampoNumero label="Margem esquerda" value={roll.manualLeftMargin} min={0} onChange={(manualLeftMargin) => setRoll({ manualLeftMargin })} />
                )}
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <CampoNumero label="Ajuste X" value={roll.offsetX} onChange={(offsetX) => setRoll({ offsetX })} />
                  <CampoNumero label="Ajuste Y" value={roll.offsetY} onChange={(offsetY) => setRoll({ offsetY })} />
                </Stack>
              </Stack>
            </Paper>
            <Paper variant="outlined" sx={{ p: 2, width: { md: 280 } }}>
              <Typography variant="subtitle2" gutterBottom>Cálculo</Typography>
              <Typography variant="body2">Largura ocupada: {occupiedWidth(roll)} mm</Typography>
              <Typography variant="body2">Sobra lateral: {lateralSlack(roll)} mm</Typography>
              <Typography variant="body2">Margem esquerda: {margem.left} mm</Typography>
              <Typography variant="body2">Margem direita: {margem.right} mm</Typography>
              <Typography variant="body2">Passo vertical: {verticalPitch(roll)} mm</Typography>
              {issues.filter((i) => !i.elementId).map((i) => (
                <Alert key={i.msg} severity={i.level === 'erro' ? 'error' : 'warning'} sx={{ mt: 1 }}>{i.msg}</Alert>
              ))}
            </Paper>
          </Stack>
        )}

        {passo === 1 && (
          <Box sx={{
            flex: 1,
            minHeight: 0,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', lg: '220px minmax(0, 1fr) 300px' },
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
          }}
          >
            <Box sx={{ borderRight: { lg: 1 }, borderColor: 'divider', overflow: 'auto', p: 1.5 }}>
              <Typography variant="subtitle2" gutterBottom>Elementos</Typography>
              <Stack spacing={0.5} sx={{ mb: 2 }}>
                {FERRAMENTAS.map((f) => (
                  <Button
                    key={f.label}
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const el = f.make();
                      ed.setModel({ ...ed.model, elements: [...ed.model.elements, el] });
                      ed.select(el.id);
                    }}
                  >
                    {f.label}
                  </Button>
                ))}
              </Stack>
              <Typography variant="subtitle2" gutterBottom>Camadas</Typography>
              <Stack spacing={0.5}>
                {[...ed.model.elements].reverse().map((el) => (
                  <Button
                    key={el.id}
                    size="small"
                    variant={ed.selectedId === el.id ? 'contained' : 'text'}
                    onClick={() => ed.select(el.id)}
                    sx={{ justifyContent: 'flex-start' }}
                  >
                    {el.name}
                  </Button>
                ))}
              </Stack>
            </Box>
            <Box sx={{ minHeight: 480, display: 'flex', flexDirection: 'column' }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider' }}>
                <Button size="small" onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.25).toFixed(2)))}>−</Button>
                <Typography variant="caption">{Math.round(zoom * 100)}%</Typography>
                <Button size="small" onClick={() => setZoom((z) => Math.min(6, +(z + 0.25).toFixed(2)))}>+</Button>
                <Button size="small" variant={grid ? 'contained' : 'outlined'} onClick={() => setGrid((v) => !v)}>Grade</Button>
                <Button size="small" variant={snap ? 'contained' : 'outlined'} onClick={() => setSnap((v) => !v)}>Encaixe</Button>
                <Button
                  size="small"
                  variant={mostrarMacros ? 'contained' : 'outlined'}
                  onClick={() => setMostrarMacros((v) => !v)}
                >
                  {mostrarMacros ? 'Ocultar macros' : 'Exibir macros'}
                </Button>
                <Button size="small" disabled={!ed.canUndo} onClick={ed.undo}>Desfazer</Button>
                <Button size="small" disabled={!ed.canRedo} onClick={ed.redo}>Refazer</Button>
              </Stack>
              <EtiquetaCanvas
                ed={ed}
                zoom={zoom}
                setZoom={setZoom}
                grid={grid}
                snap={snap}
                data={sample}
                issueIds={issueIds}
                mostrarMacros={mostrarMacros}
              />
            </Box>
            <Box sx={{ borderLeft: { lg: 1 }, borderColor: 'divider', overflow: 'auto' }}>
              <Propriedades ed={ed} issues={issues} />
            </Box>
          </Box>
        )}

        {passo === 2 && (
          <Stack spacing={2} sx={{ overflow: 'auto' }}>
            <TextField
              label="Nome do modelo"
              value={descricao}
              onChange={(e) => { setDescricao(e.target.value); setNomeAlterado(true); }}
              required
            />
            <CampoNumero
              label="Caracteres da descrição"
              suffix="car."
              min={0}
              step={1}
              value={Number(ed.model.erp.tamanhoDescricao || 0)}
              onChange={(v) => ed.setModel({
                ...ed.model,
                erp: { ...ed.model.erp, tamanhoDescricao: v > 0 ? String(Math.round(v)) : '' },
              })}
            />
            <Typography variant="body2" color="text.secondary">
              O desenho usa {'{produto1.…}'}. Ao salvar, cada coluna vira {'{produto1}'}, {'{produto2}'} e {'{produto3}'} no código da impressora. A tela de gerar etiquetas continua preenchendo esses campos.
            </Typography>
            <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 360, overflow: 'auto' }}>
              <Typography component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap' }}>
                {template || 'Corrija os erros do desenho para gerar o código.'}
              </Typography>
            </Paper>
          </Stack>
        )}

        <Stack direction="row" justifyContent="space-between">
          <Button onClick={fechar}>Cancelar</Button>
          <Stack direction="row" spacing={1}>
            <Button disabled={passo === 0} onClick={() => setPasso((p) => p - 1)}>Voltar</Button>
            {passo < 2 ? (
              <Button
                variant="contained"
                disabled={passo === 0 && erros.length > 0}
                onClick={() => setPasso((p) => p + 1)}
              >
                Avançar
              </Button>
            ) : (
              <Button variant="contained" disabled={saving || !descricao.trim() || erros.length > 0} onClick={salvar}>
                Salvar modelo
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>
    </Dialog>
  );
}
